// src/components/About.js
import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

// Organized skills data
const skillsData = [
  "Digital Strategy",
  "Paid Media",
  "AI Voice Agents",
  "Funnel Architecture",
  "Marketing Automation",
  "Prompt Engineering",
  "CRM Management",
  "Lead Generation",
  "Performance Marketing",
  "Attribution Modeling",
  "Conversion Optimization",
  "Full-Stack Product",
  "Campaign Analytics",
  "Audience Development",
  "Website Design"
];

// Organized tools data
const toolsData = [
  "Meta Ads",
  "Google Ads",
  "Snapchat Ads",
  "Microsoft Ads",
  "Connected TV Ads",
  "YouTube Ads",
  "Taboola Ads",
  "Outbrain Ads", 
  "App Install Ads",
  "Reddit Ads",
  "X ads",
  "Pinterest Ads",
  "TikTok Ads",
  "LinkedIn Ads",
  "HubSpot",
  "Klaviyo",
  "GoHighLevel",
  "LeadsHook",
  "Google Analytics",
  "GTM",
  "Zapier",
  "Claude / Claude Code",
  "OpenAI API",
  "Supabase",
  "React / Node.js",
  "MCP Servers"
];

const About = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  
  // Add state for copied email/phone
  const [emailCopied, setEmailCopied] = useState(false);
  const [phoneCopied, setPhoneCopied] = useState(false);
  
  // Email copy handler
  const handleCopyEmail = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText("isabellaibeji@outlook.com");
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 2000);
  };
  
  // Phone copy handler
  const handleCopyPhone = (e) => {
    e.preventDefault();
    navigator.clipboard.writeText("+2349020489434");
    setPhoneCopied(true);
    setTimeout(() => setPhoneCopied(false), 2000);
  };
  
  return (
    <AboutSection id="about">
      <Container>
        <AboutGrid ref={ref}>
          <AboutContent>
            <SectionHeader
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6 }}
            >
              <Eyebrow>The person behind the systems</Eyebrow>
              <Title className="gradient-text-secondary">About Me</Title>
              <Divider className="gradient-bg-secondary" />
            </SectionHeader>
            
            <AboutTextContainer
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="bg-glass"
            >
              <AboutText>
                <p>I am a growth strategist, AI product builder, and digital marketing leader with a decade of experience driving business outcomes across MarTech, LegalTech, FinTech, E-commerce, healthcare, and other niched sectors.</p>
                <p>My expertise lies in building acquisition frameworks, shipping AI-powered products end-to-end, and scaling performance systems that connect marketing, product, and technology into tangible commercial results — increasingly by wiring AI (voice agents, automation, and data pipelines) directly into the funnel.</p>
                <p>I've led strategic initiatives at companies like TSEG, where I transitioned all marketing operations in-house, built performance tracking infrastructure, and scaled high-volume legal ad campaigns across 100+ client accounts managing multi-million dollar budgets. At Snappy Kraken, I developed lead generation systems and qualification flows that reduced cost per lead by over 400% and drove thousands of leads per quarter for financial advisors. I also served as a Business Education Trainer with Meta, where I designed and delivered advanced platform training, resulting in certification rates of up to 100% and enabling Sub-Saharan African agency partners to navigate evolving advertising and privacy frameworks effectively.</p>
                <p>My work spans performance marketing, funnel architecture, website development, lead scoring systems, CRM optimization, tracking automation, and user retention strategies across the United States, Canada, Nigeria, and Sub-Saharan Africa.</p>
              </AboutText>
              
              <SkillsContainer>
                <SkillsTitle>Areas of Expertise</SkillsTitle>
                <SkillsGrid>
                  {skillsData.map((skill) => (
                    <Skill key={skill}>{skill}</Skill>
                  ))}
                </SkillsGrid>
              </SkillsContainer>
              
              <ToolsContainer>
                <ToolsTitle>Tools & Platforms</ToolsTitle>
                <ToolsGrid>
                  {toolsData.map((tool) => (
                    <Tool key={tool}>{tool}</Tool>
                  ))}
                </ToolsGrid>
              </ToolsContainer>
            </AboutTextContainer>
          </AboutContent>
          
          <AboutImageContainer>
            <ProfilePhotoContainer
              as={motion.div}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={inView ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <ProfilePhotoBorder>
                <ProfilePhotoInner />
              </ProfilePhotoBorder>
            </ProfilePhotoContainer>
            
            <SocialLinks
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.6 }}
            >
              <SocialLink href="https://linkedin.com/in/isabellaibeji" target="_blank">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M16 8a6 6 0 0 1 6 6v7h-4v-7a2 2 0 0 0-2-2 2 2 0 0 0-2 2v7h-4v-7a6 6 0 0 1 6-6z"></path>
                  <rect x="2" y="9" width="4" height="12"></rect>
                  <circle cx="4" cy="4" r="2"></circle>
                </svg>
              </SocialLink>
              <SocialLink href="https://twitter.com/isabellaibeji" target="_blank">
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M23 3a10.9 10.9 0 0 1-3.14 1.53 4.48 4.48 0 0 0-7.86 3v1A10.66 10.66 0 0 1 3 4s-4 9 5 13a11.64 11.64 0 0 1-7 2c9 5 20 0 20-11.5a4.5 4.5 0 0 0-.08-.83A7.72 7.72 0 0 0 23 3z"></path>
                </svg>
              </SocialLink>
              <SocialLink 
                onClick={handleCopyEmail}
                href="#"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                  <polyline points="22,6 12,13 2,6"></polyline>
                </svg>
              </SocialLink>
            </SocialLinks>
            
            <ContactInfo
              as={motion.div}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.6, delay: 0.8 }}
            >
              <ContactItem>
                <ContactIconContainer
                  onClick={handleCopyPhone}
                  style={{ cursor: 'pointer' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                  </svg>
                </ContactIconContainer>
                <PhoneDisplay>
                  <span>+234 902 048 9434</span>
                  <CopyButton onClick={handleCopyPhone}>
                    {phoneCopied ? "Copied!" : "Copy"}
                  </CopyButton>
                </PhoneDisplay>
              </ContactItem>
              <ContactItem>
                <ContactIconContainer
                  onClick={handleCopyEmail}
                  style={{ cursor: 'pointer' }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                    <polyline points="22,6 12,13 2,6"></polyline>
                  </svg>
                </ContactIconContainer>
                <EmailDisplay>
                  <span>isabellaibeji [at] outlook [dot] com</span>
                  <CopyButton onClick={handleCopyEmail}>
                    {emailCopied ? "Copied!" : "Copy"}
                  </CopyButton>
                </EmailDisplay>
              </ContactItem>
              <ContactItem>
                <ContactIconContainer>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                    <circle cx="12" cy="10" r="3"></circle>
                  </svg>
                </ContactIconContainer>
                <ContactText>Lagos, Nigeria</ContactText>
              </ContactItem>
            </ContactInfo>
          </AboutImageContainer>
        </AboutGrid>
      </Container>
    </AboutSection>
  );
};

// Styled components
const AboutSection = styled.section`
  padding: 6rem 0;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 4rem 0;
  }
`;

const Container = styled.div`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 2rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 1.5rem;
  }
`;

const AboutGrid = styled.div`
  display: grid;
  grid-template-columns: 3fr 2fr;
  gap: 4rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    grid-template-columns: 1fr;
    gap: 3rem;
  }
`;

const AboutContent = styled.div``;

const SectionHeader = styled.div`
  margin-bottom: 2rem;
  text-align: left;
`;

const Eyebrow = styled.div`
  font-size: 0.8rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #06b6d4;
  margin-bottom: 0.85rem;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    font-size: 2rem;
  }
`;

const Divider = styled.div`
  width: 6rem;
  height: 4px;
  border-radius: 2px;
  margin-bottom: 1.5rem;
`;

const AboutTextContainer = styled.div`
  padding: 2.5rem;
  border-radius: 16px;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 1.5rem; // Smaller padding on mobile
  }
`;

const AboutText = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: 2rem;
  
  p {
    margin-bottom: 1.5rem;
  }
  
  p:last-child {
    margin-bottom: 0;
  }
`;

const SkillsContainer = styled.div`
  margin-bottom: 2rem;
`;

const SkillsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

// Optimized for mobile
const SkillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 0.75rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: 1fr; // Single column on small mobile devices
  }
`;

const Skill = styled.span`
  padding: 0.5rem 1rem;
  background: ${({ theme }) => theme.gradients.secondary};
  border-radius: 9999px;
  font-size: 0.875rem;
  font-weight: 500;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  min-height: 36px; // Minimum height instead of fixed height
  white-space: normal; // Allow text to wrap
  word-break: break-word; // Break long words if needed
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.6rem 1.2rem; // Slightly larger padding on mobile
    font-size: 0.95rem; // Slightly larger font on mobile
  }
`;

const ToolsContainer = styled.div``;

const ToolsTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.text};
`;

// Optimized for mobile
const ToolsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
  gap: 0.75rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    grid-template-columns: repeat(2, 1fr); // Always 2 columns on mobile
    gap: 0.5rem; // Slightly smaller gap on mobile
  }
`;

const Tool = styled.span`
  padding: 0.5rem;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  font-size: 0.875rem;
  text-align: center;
  min-height: 36px; // Fixed minimum height
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    padding: 0.5rem 0.25rem; // Smaller horizontal padding on mobile
    font-size: 0.8rem; // Slightly smaller font on mobile
  }
`;

const AboutImageContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2rem;
`;

const ProfilePhotoContainer = styled.div`
  width: 16rem;
  height: 16rem;
  position: relative;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    width: 12rem; // Smaller photo on very small screens
    height: 12rem;
  }
`;

const ProfilePhotoBorder = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  padding: 0.5rem;
  background: ${({ theme }) => theme.gradients.primary};
  animation: rotate 10s linear infinite;
  
  @keyframes rotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ProfilePhotoInner = styled.div`
  width: 100%;
  height: 100%;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.dark};
  
  /* Update with your profile image */
  background-image: url('/Isabella_Ibeji.jpg');
  background-size: cover;
  background-position: center;
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1.5rem;
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
  color: ${({ theme }) => theme.colors.textSecondary};
  transition: all ${({ theme }) => theme.transitions.default};
  cursor: pointer;
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.colors.text};
    transform: translateY(-2px);
  }
`;

const ContactInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
`;

const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ContactIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.05);
`;

const ContactText = styled.span`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
`;

// Email and Phone display with copy buttons
const EmailDisplay = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.9375rem;
  
  span {
    transition: color ${({ theme }) => theme.transitions.default};
  }
  
  &:hover span {
    color: ${({ theme }) => theme.colors.text};
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.sm}) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }
`;

const PhoneDisplay = styled(EmailDisplay)`
  /* Inherits styles from EmailDisplay */
`;

const CopyButton = styled.button`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid ${({ theme }) => theme.colors.border};
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: 0.75rem;
  padding: 0.1rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.default};
  
  &:hover {
    background: rgba(255, 255, 255, 0.1);
    color: ${({ theme }) => theme.colors.text};
  }
`;

export default About;